import { CommonModule } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { Router, RouterLinkWithHref } from '@angular/router';
import { ModalModule, ModalService, WindowRef } from '@boels/helpers';
import { BOProduct, OrderEntry } from '@boels/models';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faClone,
  faFileLines,
  faPencil,
  faShoppingBasket,
} from '@fortawesome/free-solid-svg-icons';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { AddToCartModalComponent } from '../add-to-cart-modal/add-to-cart-modal.component';
import { RequestQuoteModalComponent } from '../request-quote-modal/request-quote-modal.component';
import { AddToCartModalData, CultureFacade } from '@boels/shared-state';
import { take } from 'rxjs';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { ButtonComponent } from '../button/button.component';

export enum AddToCartButtonTypes {
  AddToCart = 'add-to-cart',
  RequestQuote = 'request-quote',
  Variants = 'variants',
  Edit = 'edit',
}

export enum AddToCartButtonClass {
  ProductCard = 'product-card-btn',
  ProductSummary = 'product-summary-btn',
}

export interface ButtonConfig {
  variant?: 'main' | 'primary-brand' | 'primary-black' | 'secondary';
  icon?: IconDefinition;
  shape?: 'square' | 'pill';
  size?: 'md' | 'sm';
  mode?: 'base' | 'inverted' | 'brand';
  action?: 'upward' | 'downward' | 'next' | 'previous' | 'none';
  alwaysShowActionIcon?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
}

@Component({
  selector: 'boels-add-to-cart-button',
  templateUrl: './add-to-cart-button.component.html',
  styleUrls: ['./add-to-cart-button.component.scss'],
  imports: [
    FontAwesomeModule,
    TranslocoModule,
    CommonModule,
    RouterLinkWithHref,
    ModalModule,
    ButtonComponent,
  ],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['addToCartButton'],
    },
  ],
})
export class AddToCartButtonComponent {
  /** Injected services */
  #modalService = inject(ModalService);
  #router = inject(Router);
  #winRef = inject(WindowRef);
  #cultureFacade = inject(CultureFacade);

  faShoppingBasketIcon = faShoppingBasket;
  faCloneIcon = faClone;
  faFileLines = faFileLines;
  faEditIcon = faPencil;
  btnTypes = AddToCartButtonTypes;

  $product = input.required<BOProduct>();
  $orderEntry = input.required<OrderEntry | undefined>();
  $buttonType = computed(() =>
    this.getButtonType(this.$product(), this.$orderEntry()),
  );
  $buttonConfig = input.required<ButtonConfig>();

  getButtonType(
    product: BOProduct,
    orderEntry?: OrderEntry,
  ): AddToCartButtonTypes {
    if (this.#isAddToCartButtonType(product)) {
      if (orderEntry) return AddToCartButtonTypes.Edit;
      return AddToCartButtonTypes.AddToCart;
    } else if (this.#isVariantButtonType(product)) {
      return AddToCartButtonTypes.Variants;
    } else {
      return AddToCartButtonTypes.RequestQuote;
    }
  }

  addToCartModal(entry?: OrderEntry): void {
    this.#modalService.open(AddToCartModalComponent, {
      panelClass: 'modal-accessories-view',
      data: {
        product: this.$product(),
        entry: entry,
      } as AddToCartModalData,
    });
  }

  onRequestQuoteButtonClick(): void {
    if (this.$product().quotationPageLabel) {
      this.#openRequestQuoteForm();
    } else {
      this.#openRequestQuoteModal();
    }
  }

  #isAddToCartButtonType(product: BOProduct): boolean {
    return (
      !product.variantOptions &&
      !product.quotationPageLabel &&
      product.stock?.stockLevelStatus !== 'outOfStock' &&
      !!product.price
    );
  }

  #isVariantButtonType(product: BOProduct): boolean {
    return (
      !!product.variantOptions && !!product.url && !product.quotationPageLabel
    );
  }

  #openRequestQuoteForm() {
    if (this.$product().quotationPageLabel) {
      this.#cultureFacade
        .getActiveCulture()
        .pipe(take(1))
        .subscribe((culture) => {
          const queryParams = {
            product: this.$product().code,
          };

          const url = this.#router.serializeUrl(
            this.#router.createUrlTree(
              [
                culture,
                ...(this.$product()
                  .quotationPageLabel?.substring(1)
                  .split('/') ?? ''),
              ],
              {
                queryParams,
              },
            ),
          );
          this.#winRef.nativeWindow?.open(url, '_blank');
        });
    }
  }

  #openRequestQuoteModal() {
    const modal = this.#modalService.open(RequestQuoteModalComponent, {
      panelClass: 'modal-sm',
    });
    modal.componentInstance.phoneNumber =
      this.$product().pointOfService?.address?.phone;
    modal.componentInstance.phoneRate =
      this.$product().pointOfService?.address?.phoneRate;
    modal.componentInstance.email =
      this.$product().pointOfService?.address?.email;
  }
}
